<template>
  <div v-tooltip="dropdownTooltip" data-testid="insights-actions-dropdown">
    <SfDropdown
      :dropdown-id="dropdownId"
      :is-open="isDropdownOpen"
      :is-disabled="isDropdownDisabled"
      :variant="dropdownVariant"
      :drawer-direction="drawerDirection"
      @dropdown-clicked="toggleDropdown"
      @dropdown-closed="closeDropdown"
    >
      <!-- Trigger Button -->
      <template #trigger>
        <div class="sf-level">
          <span>{{ actionsLabel }}</span>
          <div
            v-if="isActionRunningByCurrentUser"
            class="sf-spinner tw-border-gray-200"
          />
        </div>
      </template>

      <!-- Dropdown Menu -->
      <template #menu>
        <SfDropdownItemHeader>
          <div>{{ runActionLabel }}</div>
        </SfDropdownItemHeader>

        <!-- Archive workflows -->
        <SfDropdownItem
          v-if="!isLite && isAdmin"
          :is-tall="true"
          data-testid="insights-bulk-actions-archive-workflows"
          @click="archiveWorkflowModal"
        >
          <div>{{ I18n.t('companies_overview.actions.archive_workflow') }}</div>
        </SfDropdownItem>

        <!-- Download as Excel -->
        <SfDropdownItem
          :is-tall="true"
          data-testid="insights-download-as-excel"
          :class="handleDownloadExcelClass"
          @click="handleDownloadExcelClick"
        >
          <div v-tooltip="downloadAsExcelTooltip">
            <div :class="downloadAsExcelClasses">
              {{ downloadAsExcelTitle }}
            </div>
          </div>
        </SfDropdownItem>

        <SfDropdownItem
          v-if="!isLite && isAdmin"
          v-tooltip.placementLeft.textLeft="lockPeriodDisabledTooltip"
          :is-tall="true"
          :is-disabled="!hasPeriodSelection || !!activeLockPeriodAction"
          data-testid="insights-bulk-actions-lock-period"
          @click="handleLockPeriodClick"
        >
          <div>
            {{ I18n.t('companies_overview.actions.lock_period.action_name') }}
          </div>
        </SfDropdownItem>

        <!-- Send a Message -->
        <SfDropdownItem
          v-if="!isLite"
          v-tooltip.placementLeft.textLeft="periodNotSelectedTooltip"
          :is-tall="true"
          :is-disabled="!hasPeriodSelection"
          data-testid="insights-bulk-actions-send-message"
          @click="handleSendMessageClick"
        >
          <div>{{ I18n.t('companies_overview.actions.send_a_message') }}</div>
        </SfDropdownItem>

        <!-- Running Actions -->
        <template v-if="isActionRunning">
          <SfDropdownItemHeader>
            <div>
              {{ I18n.t('companies_overview.actions.actions_in_progress') }}
            </div>
          </SfDropdownItemHeader>

          <SfDropdownItem
            v-for="action in activeActions"
            :key="action.insight_action_run_id"
            class="sf-nowrap"
            @click="handleRunningActionClick(action)"
          >
            <span>{{ actionDisplayName(action.action_name) }}</span>
            <span class="sf-spinner sf-spinner-primary tw-mx-2"></span>
            <span v-if="action.processed_count && action.total_count">
              <strong>
                {{ action.processed_count }}
              </strong>
              <span>/{{ action.total_count }}</span>
            </span>
          </SfDropdownItem>
        </template>
      </template>
    </SfDropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { storeToRefs } from 'pinia'
import I18n from 'i18n-js'

import { useDropdown } from '@app/shared/pinia/dropdown'
import { dropdowns } from '@app/shared/types/dropdown'
import { useBulkActionsStore } from '../../store/bulk-actions'
import { useCompaniesStore } from '../../store/companies'
import { useInsightsRootStore } from '../../store/insights-root'
import type { Job } from '../../types/index'
import {
  SfDropdown,
  SfDropdownItem,
  SfDropdownItemHeader,
} from '@app/shared/vue/components/sf-dropdown'
import { drawerDirections } from '@app/shared/vue/components/sf-dropdown/constants'

import {
  CREATE_REMARK_ACTION,
  DOWNLOAD_AS_EXCEL_ACTION,
  CREATE_REMARK_ACTIONS,
  CREATE_REMARK_ON_COMPANY_ACTION,
  ARCHIVE_WORKFLOW_ACTION,
  LOCK_PERIOD_ACTION,
} from './constants'
import { getCurrentUserId } from '@app/adapters/silverfin'

// ---------- Store & Pinia Setup ----------
const bulkActionsStore = useBulkActionsStore()
const companiesStore = useCompaniesStore()
const rootStore = useInsightsRootStore()

const dropdownStore = useDropdown()
const { currentDropdownId } = storeToRefs(dropdownStore)
const isLite = inject('isLite')

// ---------- Props & Emits ----------
// The trigger sits on the left of the old toolbar but on the right of the
// query toolbar, where a right-opening menu gets clipped by the viewport.
withDefaults(defineProps<{ drawerDirection?: string }>(), {
  drawerDirection: drawerDirections.right,
})

const emit = defineEmits(['actionSelected'])

// ---------- Computed Properties ----------
const selectedFileCount = computed(() => companiesStore.selectedCompanies.size)
const isLoading = computed(() => !companiesStore.allCompaniesProcessed)
const hasPeriodSelection = computed(() => rootStore.periodSelection)
const isDropdownDisabled = computed(
  () => selectedFileCount.value === 0 || isLoading.value
)
const dropdownVariant = computed(() =>
  isDropdownDisabled.value ? 'neutral' : 'primary'
)

const isActionRunning = computed(() => bulkActionsStore.isActionRunning)
const isActionRunningByCurrentUser = computed(
  () => bulkActionsStore.actionsInProgressByCurrentUser.length > 0
)
const activeActions = computed(() => bulkActionsStore.actionsInProgress)
const activeLockPeriodAction = computed(() =>
  bulkActionsStore.actionsInProgress.find(
    (action) => action.action_name === LOCK_PERIOD_ACTION
  )
)
const dropdownId = dropdowns.INSIGHTS_ACTIONS
const isDropdownOpen = computed(() => currentDropdownId.value === dropdownId)
const periodNotSelectedTooltip = computed(() =>
  hasPeriodSelection.value
    ? ''
    : I18n.t('companies_overview.actions.select_a_period_to_run_action')
)

const lockPeriodDisabledTooltip = computed(() => {
  if (!hasPeriodSelection.value) {
    return I18n.t('companies_overview.actions.select_a_period_to_run_action')
  }

  if (activeLockPeriodAction.value) {
    const username =
      activeLockPeriodAction.value.user_id ===
      parseInt(getCurrentUserId() || '0', 10)
        ? I18n.t('user.you')
        : activeLockPeriodAction.value.username

    return I18n.t('companies_overview.actions.lock_period.already_running', {
      user: username,
      current: activeLockPeriodAction.value.processed_count,
      total: activeLockPeriodAction.value.total_count,
    })
  }

  return ''
})

// ---------- Labels & i18n ----------
const actionsLabel = I18n.t('report/template.sidebar.actions')
const companiesFullyLoaded = computed(() => companiesStore.companiesFullyLoaded)

const downloadAsExcelTitle = I18n.t(
  'companies_overview.progress_v2.download_as_xlsx'
)
const downloadAsExcelClasses = computed(() =>
  companiesFullyLoaded.value ? '' : 'sf-disabled'
)
const downloadAsExcelTooltip = computed(() =>
  companiesFullyLoaded.value
    ? ''
    : I18n.t('companies_overview.progress_v2.download_as_xlsx_disabled_tooltip')
)

const runActionLabel = computed(() =>
  selectedFileCount.value === 1
    ? I18n.t('companies_overview.progress_v2.run_action_for_one_file')
    : I18n.t('companies_overview.progress_v2.run_action_for_multiple_files', {
        number: selectedFileCount.value,
      })
)

const dropdownTooltip = computed(() => {
  if (isLoading.value)
    return I18n.t('companies_overview.progress_v2.tooltip_loading')
  if (selectedFileCount.value === 0)
    return I18n.t('companies_overview.progress_v2.tooltip_no_files')
  return ''
})

const handleDownloadExcelClass = computed(() =>
  !companiesFullyLoaded.value ? '!tw-cursor-not-allowed' : ''
)

// ---------- Methods ----------
const toggleDropdown = () => dropdownStore.toggleDropdown(dropdownId)
const closeDropdown = () => dropdownStore.closeDropdown()

const handleSendMessageClick = (payloadOverride = {}) => {
  emit('actionSelected', {
    action: CREATE_REMARK_ACTION,
    payload: {
      message: '',
      type: 'note',
      readOnly: false,
      selectedTemplateId: null,
      actionName: CREATE_REMARK_ON_COMPANY_ACTION,
      tagIds: [],
      ...payloadOverride,
    },
  })
  closeDropdown()
}

const handleDownloadExcelClick = () => {
  if (!companiesFullyLoaded.value) {
    return
  }

  emit('actionSelected', { action: DOWNLOAD_AS_EXCEL_ACTION })
  closeDropdown()
}

const handleArchiveWorkflowClick = (payloadOverride = {}) => {
  emit('actionSelected', {
    action: ARCHIVE_WORKFLOW_ACTION,
    payload: {
      readOnly: false,
      selectedWorkflowId: null,
      ...payloadOverride,
    },
  })
  closeDropdown()
}

const actionDisplayName = (actionName: string | undefined) => {
  if (!actionName) {
    return ''
  }

  if (CREATE_REMARK_ACTIONS.includes(actionName)) {
    return I18n.t('companies_overview.actions.send_a_message')
  }

  if (actionName === ARCHIVE_WORKFLOW_ACTION) {
    return I18n.t('companies_overview.actions.archive_workflow')
  }

  if (actionName === LOCK_PERIOD_ACTION) {
    return I18n.t('companies_overview.actions.lock_period.action_name')
  }

  return actionName
}

const handleRunningActionClick = (action: Job) => {
  if (
    action.action_name &&
    CREATE_REMARK_ACTIONS.includes(action.action_name)
  ) {
    const payload = {
      message: action.payload.text,
      type: action.payload.type,
      selectedTemplateId: action.payload.template_ids,
      tagIds: action.payload.tag_ids,
      readOnly: true,
      actionName: action.action_name,
    }
    handleSendMessageClick(payload)
  }

  if (action.action_name === ARCHIVE_WORKFLOW_ACTION) {
    const payload = {
      selectedWorkflowId: action.payload.firm_workflow_id,
      readOnly: true,
    }
    handleArchiveWorkflowClick(payload)
  }

  if (action.action_name === LOCK_PERIOD_ACTION) {
    const payload = {
      selectedLockingType: action.payload.locking_type,
      readOnly: true,
    }
    handleLockPeriodClick(payload)
  }
}

const archiveWorkflowModal = () => {
  emit('actionSelected', {
    action: ARCHIVE_WORKFLOW_ACTION,
    payload: {
      actionName: ARCHIVE_WORKFLOW_ACTION,
    },
  })
  dropdownStore.closeDropdown()
}

const handleLockPeriodClick = (payloadOverride = {}) => {
  emit('actionSelected', {
    action: LOCK_PERIOD_ACTION,
    payload: {
      actionName: LOCK_PERIOD_ACTION,
      ...payloadOverride,
    },
  })
  dropdownStore.closeDropdown()
}

const isAdmin = computed(() => {
  return window.Silverfin.isCurrentUserAdmin()
})
</script>
